/**
 * applyLineEdit: merge an edit onto an existing quote line (IDI-365).
 *
 * The edited line keeps its identity (id, accessories, alternates, anything
 * the edit doesn't carry) and takes every edited field. Cached price fields
 * (lineTotal / line_total / cost_basis) are dropped, so the margin engine
 * re-derives the price from the new cost and the line's resolved margin,
 * exactly as for a freshly added line. Leaving them made the stale total
 * read as a price override (walk v6: "4% price override", GM 3.9%).
 *
 * Same rule as repriceOpenQuoteLines, which drops them for the same reason.
 */
const CACHED_PRICE_FIELDS = ['lineTotal', 'line_total', 'cost_basis'];

/**
 * IDI-309: drop a one-time estimate-card re-price (distributeToTarget writes
 * lineTotal + marginSource:'price_override'). Editing the line afterwards
 * snaps it back to its own margin tier (09-11): no standing override.
 */
function dropTargetPrice(line) {
  const next = { ...line };
  CACHED_PRICE_FIELDS.forEach((k) => { delete next[k]; });
  if (next.marginSource === 'price_override') delete next.marginSource;
  return next;
}

/**
 * IDI-534: fields that don't touch the price. An edit that carries ONLY
 * these (e.g. adding a note from the NoteModal) keeps the line's price,
 * including a target re-price: it is not a pricing edit, so nothing snaps
 * back.
 */
const NON_PRICING_FIELDS = new Set(['notes', 'note', 'internalNotes', 'internal_notes']);

export function isNonPricingEdit(updates) {
  if (!updates || typeof updates !== 'object') return false;
  const keys = Object.keys(updates);
  return keys.length > 0 && keys.every((k) => NON_PRICING_FIELDS.has(k));
}

export function applyLineEdit(line, updates) {
  if (isNonPricingEdit(updates)) return { ...line, ...updates };
  return dropTargetPrice({ ...line, ...updates });
}

export { dropTargetPrice };

export default applyLineEdit;
